import axios from "axios";
import { getApiBaseUrl } from "./site-profile";
import { ISLAND_BY_CODE } from "./caribbeanIslands";
import { getIslandDivisions, mergeDivisionList } from "./island-divisions";
import { getMeetupMapFallback } from "./meetup-map-fallback";
import { applyTierToSpots } from "./tier-spots";

function apiBases() {
  const bases = [
    getApiBaseUrl(),
    process.env.REACT_APP_BACKEND_URL
      ? `${String(process.env.REACT_APP_BACKEND_URL).replace(/\/$/, "")}/api`
      : null,
  ].filter(Boolean);
  return [...new Set(bases)];
}

/** Instant island shell — center, zoom, parish/district list (no network). */
export function getIslandContextSync(countryCode) {
  const code = (countryCode || "barbados").toLowerCase().trim();
  const island = ISLAND_BY_CODE[code];
  const div = getIslandDivisions(code);
  return {
    country: code,
    island: island?.name || code,
    center: island?.center || ISLAND_BY_CODE.barbados.center,
    zoom: island?.zoom ?? 11,
    division_label: div.division_label,
    divisions: div.divisions,
    source: "local",
  };
}

export async function fetchIslandContext(countryCode) {
  const code = (countryCode || "barbados").toLowerCase().trim();
  for (const base of apiBases()) {
    try {
      const res = await axios.get(`${base}/islands/${code}/context`, {
        withCredentials: true,
        timeout: 8000,
      });
      if (res.data?.country) {
        return { ...res.data, source: "api" };
      }
    } catch (_err) {
      // try next origin
    }
  }
  return getIslandContextSync(code);
}

function pinToActivity(pin) {
  const images = pin.images || [];
  return {
    activity_id: pin.pin_id,
    name: pin.name,
    description: pin.description || "",
    short_description: pin.description || "",
    category: pin.category || pin.kind || "attraction",
    country: pin.country,
    island: pin.island,
    parish: pin.parish,
    lat: pin.lat,
    lng: pin.lng,
    is_featured: pin.is_featured,
    is_premium_content: pin.tier === "PREMIUM",
    google_maps_url: pin.google_maps_url,
    rating: pin.rating,
    source: pin.source || "catalog",
    tags: pin.tags || [],
    images,
    image_url: pin.image_url || images[0] || null,
    image_source: pin.image_source || null,
  };
}

/** Instant island activities — all 15 countries, no network (Discovery paint). */
export function getIslandActivitiesSync({ country, parish, category, search } = {}) {
  const code = (country || "barbados").toLowerCase().trim();
  return buildFallbackActivities({ country: code, parish, category, search });
}

function buildFallbackActivities({ country, parish, category, search } = {}) {
  const payload = getMeetupMapFallback({ country, parish, category });
  let activities = (payload.pins || []).map(pinToActivity);
  if (search) {
    const q = search.trim().toLowerCase();
    activities = activities.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q) ||
        (a.parish || "").toLowerCase().includes(q)
    );
  }
  const div = mergeDivisionList(country, payload.parishes);
  return {
    country,
    island: ISLAND_BY_CODE[country]?.name || country,
    center: payload.island_center,
    zoom: payload.island_zoom,
    division_label: div.division_label,
    divisions: div.divisions,
    activities,
    total: activities.length,
    pins: payload.pins,
    stats: payload.stats,
    google_connected: false,
    source: "fallback",
  };
}

/** Unified island activities — API first, instant embedded catalog fallback. */
export async function fetchIslandActivities({
  country = "barbados",
  parish = "",
  category = "",
  search = "",
  enrich = true,
  isPro = false,
  limitReached = false,
} = {}) {
  const code = (country || "barbados").toLowerCase().trim();
  const params = {};
  if (parish) params.parish = parish;
  if (category) params.category = category;
  if (search) params.search = search;
  if (!enrich) params.enrich = false;

  for (const base of apiBases()) {
    try {
      const res = await axios.get(`${base}/islands/${code}/activities`, {
        params,
        withCredentials: true,
        timeout: 14000,
      });
      if (res.data && Array.isArray(res.data.activities)) {
        return { ...res.data, source: "api" };
      }
    } catch (_err) {
      // try next origin
    }
  }

  const fallback = buildFallbackActivities({ country: code, parish, category, search });
  fallback.activities = applyTierToSpots(
    fallback.activities.map((a) => ({ ...a, spot_id: a.activity_id })),
    { isPro, limitReached }
  ).map((s) => ({ ...s, activity_id: s.spot_id || s.activity_id }));
  fallback.total = fallback.activities.length;
  return fallback;
}

/** Island activities → spot cards with tier applied. */
export function getIslandSpotsSync({ country, category, search, isPro = false, limitReached = false } = {}) {
  const payload = getIslandActivitiesSync({ country, category, search });
  const spots = (payload.activities || []).map(activityToSpot);
  return {
    ...payload,
    spots: applyTierToSpots(spots, { isPro, limitReached }),
  };
}

/** Map activity row to spot card shape used by Discovery / Landing lists. */
export function activityToSpot(activity) {
  const locationLabel =
    activity.formatted_address ||
    [activity.parish, activity.country].filter(Boolean).join(", ") ||
    "Caribbean";

  return {
    spot_id: activity.activity_id,
    name: activity.name,
    description: activity.description || activity.short_description,
    short_description: activity.short_description || activity.description,
    category: activity.category,
    country: activity.country,
    parish: activity.parish,
    location: locationLabel,
    coordinates: {
      lat: activity.lat,
      lng: activity.lng,
      google_place_id: activity.google_place_id,
    },
    rating: activity.rating,
    is_featured: activity.is_featured,
    is_premium_content: activity.is_premium_content,
    locked: activity.locked,
    images: activity.images || [],
    image_url: activity.image_url || activity.images?.[0] || null,
    image_source: activity.image_source || null,
    google_maps_url: activity.google_maps_url,
    formatted_address: activity.formatted_address,
    tags: activity.tags || [],
  };
}
